import { useState } from 'react';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { getAdditionalUserInfo } from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import { trackLogin, trackSignUp } from '../analytics';
import { T, LogoMark, Wordmark } from '../theme';
import TermsPage from './TermsPage';

function LoginPage({ onSkip, onBack, initialSignUp = false }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(initialSignUp);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    if (isSignUp && !tosAccepted) {
      setError('Please accept the Terms of Service to create an account.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      if (isSignUp) {
        await createUserWithEmailAndPassword(auth, email, password);
        trackSignUp('email');
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        trackLogin('email');
      }
    } catch (err) {
      setError(err.message.replace('Firebase: ', ''));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err) {
      setError(err.message.replace('Firebase: ', ''));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError(null);
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const info = getAdditionalUserInfo(result);
      info?.isNewUser ? trackSignUp('google') : trackLogin('google');
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError(err.message.replace('Firebase: ', ''));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: T.gradNavy,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        fontFamily: T.fontBody,
      }}
    >
      {onBack && (
        <button
          onClick={onBack}
          style={{
            position: 'absolute', top: 20, left: 20,
            background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 10,
            color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 700,
            padding: '8px 14px', cursor: 'pointer',
          }}
        >
          ← Back
        </button>
      )}

      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ marginBottom: 14 }}><LogoMark size={56} /></div>
        <h1 style={{ margin: 0 }}><Wordmark size={28} /></h1>
        <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 14, marginTop: 8 }}>
          AI powered finances for everyone
        </p>
      </div>

      <div
        style={{
          background: '#fff',
          borderRadius: 20,
          padding: 24,
          width: '100%',
          maxWidth: 350,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 800, color: '#111' }}>
          {forgotMode ? 'Reset Password' : isSignUp ? 'Create Account' : 'Sign In'}
        </h2>

        {forgotMode && (
          <>
            {resetSent ? (
              <div style={{ background: '#F0FDF4', color: '#16A34A', padding: '12px 14px', borderRadius: 10, fontSize: 13, marginBottom: 16 }}>
                Check your inbox — a reset link has been sent to <strong>{email}</strong>.
              </div>
            ) : (
              <>
                <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 16, marginTop: 0 }}>
                  Enter your email and we'll send you a link to reset your password.
                </p>
                {error && (
                  <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: 10, fontSize: 13, marginBottom: 16 }}>
                    {error}
                  </div>
                )}
                <form onSubmit={handleForgotPassword}>
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={{ width: '100%', padding: 12, borderRadius: 10, border: '1.5px solid #E5E7EB', fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 12, fontFamily: 'inherit' }}
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', background: loading ? '#9CA3AF' : T.gradTeal, color: '#fff', fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', marginBottom: 12 }}
                  >
                    {loading ? 'Sending…' : 'Send Reset Email'}
                  </button>
                </form>
              </>
            )}
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={() => { setForgotMode(false); setResetSent(false); setError(null); }}
                style={{ background: 'none', border: 'none', color: T.tealDeep, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                ← Back to Sign In
              </button>
            </div>
          </>
        )}

        {!forgotMode && error && (
          <div
            style={{
              background: '#FEF2F2',
              color: '#DC2626',
              padding: '10px 14px',
              borderRadius: 10,
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        {!forgotMode && <form onSubmit={handleEmailAuth}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{
              width: '100%',
              padding: 12,
              borderRadius: 10,
              border: '1.5px solid #E5E7EB',
              fontSize: 14,
              outline: 'none',
              boxSizing: 'border-box',
              marginBottom: 12,
              fontFamily: 'inherit',
            }}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            style={{
              width: '100%',
              padding: 12,
              borderRadius: 10,
              border: '1.5px solid #E5E7EB',
              fontSize: 14,
              outline: 'none',
              boxSizing: 'border-box',
              marginBottom: isSignUp ? 12 : 4,
              fontFamily: 'inherit',
            }}
          />
          {!isSignUp && (
            <div style={{ textAlign: 'right', marginBottom: 12 }}>
              <button
                type="button"
                onClick={() => { setForgotMode(true); setError(null); setResetSent(false); }}
                style={{ background: 'none', border: 'none', color: T.tealDeep, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}
              >
                Forgot password?
              </button>
            </div>
          )}

          {isSignUp && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 16 }}>
              <input
                type="checkbox"
                id="tos"
                checked={tosAccepted}
                onChange={e => { setTosAccepted(e.target.checked); setError(null); }}
                style={{ marginTop: 2, flexShrink: 0, accentColor: T.tealDeep, width: 15, height: 15 }}
              />
              <label htmlFor="tos" style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.5, cursor: 'pointer' }}>
                I have read and agree to the{' '}
                <button
                  type="button"
                  onClick={() => setShowTerms(true)}
                  style={{ background: 'none', border: 'none', color: T.tealDeep, fontWeight: 700, fontSize: 12, cursor: 'pointer', padding: 0 }}
                >
                  Terms of Service
                </button>
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || (isSignUp && !tosAccepted)}
            style={{
              width: '100%',
              padding: 14,
              borderRadius: 12,
              border: 'none',
              background: (loading || (isSignUp && !tosAccepted)) ? '#9CA3AF' : T.gradTeal,
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              cursor: (loading || (isSignUp && !tosAccepted)) ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 15px rgba(5, 196, 176, 0.3)',
              marginBottom: 12,
            }}
          >
            {loading ? 'Please wait...' : isSignUp ? 'Sign Up' : 'Sign In'}
          </button>
        </form>}

        {!forgotMode && <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            margin: '4px 0 16px',
          }}
        >
          <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
          <span style={{ fontSize: 12, color: '#9CA3AF' }}>or</span>
          <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
        </div>}

        {!forgotMode && <button
          onClick={handleGoogle}
          disabled={loading}
          style={{
            width: '100%',
            padding: 14,
            borderRadius: 12,
            border: '1.5px solid #E5E7EB',
            background: '#fff',
            fontSize: 14,
            fontWeight: 700,
            cursor: loading ? 'not-allowed' : 'pointer',
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            marginBottom: 16,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
          </svg>
          Continue with Google
        </button>}

        {!forgotMode && isSignUp && (
          <p style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center', margin: '0 0 8px', lineHeight: 1.5 }}>
            By continuing with Google you also agree to our{' '}
            <button
              type="button"
              onClick={() => setShowTerms(true)}
              style={{ background: 'none', border: 'none', color: T.tealDeep, fontWeight: 600, fontSize: 11, cursor: 'pointer', padding: 0 }}
            >
              Terms of Service
            </button>.
          </p>
        )}

        {!forgotMode && (
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={() => {
                setIsSignUp(!isSignUp);
                setError(null);
                setTosAccepted(false);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: T.tealDeep,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: 8,
              }}
            >
              {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
            </button>
          </div>
        )}
      </div>

      {showTerms && <TermsPage onClose={() => setShowTerms(false)} />}
    </div>
  );
}

export default LoginPage;
